// Calculo do tamanho dos tiles da Sala de voz.
//
// A regra da tela e "cabe sempre, nunca rola": o palco tem altura fixa e sao
// os tiles que encolhem. Este modulo resolve so a matematica disso — fica
// separado da pagina por ser logica pura (e testavel sem DOM).

// Espacamento entre tiles, em px. Precisa bater com o `gap` aplicado no
// .sala-grid, porque entra na conta do espaco disponivel.
export const GAP = 10;

export const PROPORCAO = 16 / 9;

// Escolhe quantas colunas maximizam o tamanho dos tiles dentro do espaco
// disponivel, mantendo 16:9. Testa todas as divisoes possiveis — com no
// maximo 5 participantes isso e trivial e sempre acha o otimo.
//
// Devolve null quando ainda nao ha espaco medido (primeiro render, antes do
// ResizeObserver responder).
export function calcularGrade(total, largura, altura) {
  if (!total || largura < 80 || altura < 60) return null;

  let melhor = null;
  for (let colunas = 1; colunas <= total; colunas += 1) {
    const linhas = Math.ceil(total / colunas);
    const cabeNaLargura = (largura - GAP * (colunas - 1)) / colunas;
    const cabeNaAltura = ((altura - GAP * (linhas - 1)) / linhas) * PROPORCAO;
    const larguraTile = Math.min(cabeNaLargura, cabeNaAltura);
    if (larguraTile > 0 && (!melhor || larguraTile > melhor.largura)) {
      melhor = {
        colunas,
        linhas,
        largura: larguraTile,
        altura: larguraTile / PROPORCAO,
      };
    }
  }
  if (!melhor) return null;

  return {
    ...melhor,
    // Largura de uma linha cheia — usada para centralizar a ultima linha
    // incompleta sem espalhar os tiles ate as bordas.
    larguraFaixa: melhor.colunas * melhor.largura + GAP * (melhor.colunas - 1),
  };
}

// Largura desejada de uma miniatura no modo destaque. MINI_MIN e uma
// preferencia, nao um limite rigido: se nao couber, a faixa encolhe em vez de
// sumir — esconder gente da sala e pior do que miniatura pequena.
// Largura de referencia da miniatura na FASE 1, que decide orientacao e
// tamanho do destaque.
//
// PROPORCIONAL a tela, nao fixa em 200px. Com 200 fixos, em monitor grande a
// faixa ficava com metade da altura vazia e as pessoas minusculas ao lado de
// uma tela enorme. Cresce a miniatura ~28% num monitor de 1500px e custa ~4%
// do destaque — trade que o time pediu olhando a tela.
const MINI_BASE_FRACAO = 0.17;
const MINI_BASE_MIN = 160;
const MINI_BASE_MAX = 300;

function miniBase(largura) {
  return Math.min(Math.max(largura * MINI_BASE_FRACAO, MINI_BASE_MIN), MINI_BASE_MAX);
}

// Teto da FASE 2, que so cresce a miniatura para dentro de espaco que o
// destaque nao ia usar. Por isso pode ser generoso.
const MINI_MAX = 340;

// Mesmo com espaco de sobra, a miniatura nunca passa desta fracao do
// destaque: senao a hierarquia se perde e nao da mais para dizer quem esta em
// foco.
const MINI_FRACAO_DO_DESTAQUE = 0.45;

// Maior retangulo 16:9 que cabe num espaco.
function ajustar(largura, altura) {
  if (largura <= 0 || altura <= 0) return null;
  const w = Math.min(largura, altura * PROPORCAO);
  if (w <= 0) return null;
  return { largura: w, altura: w / PROPORCAO };
}

// Layout "destaque" (spotlight), no espirito do Meet: um participante ocupa o
// palco e os demais viram uma faixa de miniaturas. A faixa vai a DIREITA ou
// EMBAIXO — escolhemos a orientacao que deixa o destaque maior, porque o que
// sobra depende do formato da janela: janela larga e baixa perde pouco com a
// faixa lateral; janela alta perde pouco com a faixa embaixo.
//
// O calculo tem DUAS FASES:
//
//   1. escolhe a orientacao e o tamanho do destaque, como antes;
//   2. CRESCE as miniaturas para dentro do espaco que o destaque nao usou.
//
// A fase 2 existe porque o destaque quase sempre fica limitado por um dos
// eixos (normalmente a altura), e o outro eixo sobrava vazio. Como crescer a
// miniatura ali nao tira nada do destaque, era espaco jogado fora.
export function calcularDestaque(qtdMiniaturas, largura, altura) {
  if (largura < 80 || altura < 60) return null;

  const sozinho = () => {
    const principal = ajustar(largura, altura);
    return principal ? { orientacao: 'nenhuma', principal, miniatura: null } : null;
  };

  if (qtdMiniaturas <= 0) return sozinho();

  const n = qtdMiniaturas;
  const opcoes = [];

  // Faixa a direita: miniaturas empilhadas. A altura disponivel e que limita.
  const larguraCabeNaColuna = ((altura - GAP * (n - 1)) * PROPORCAO) / n;
  const base = miniBase(largura);
  const miniColuna = Math.min(base, larguraCabeNaColuna);
  if (miniColuna > 0) {
    const principal = ajustar(largura - miniColuna - GAP, altura);
    if (principal) opcoes.push({ orientacao: 'direita', principal, mini: miniColuna });
  }

  // Faixa embaixo: miniaturas lado a lado. A largura disponivel e que limita.
  const larguraCabeNaLinha = (largura - GAP * (n - 1)) / n;
  const miniLinha = Math.min(base, larguraCabeNaLinha);
  if (miniLinha > 0) {
    const principal = ajustar(largura, altura - miniLinha / PROPORCAO - GAP);
    if (principal) opcoes.push({ orientacao: 'baixo', principal, mini: miniLinha });
  }

  if (!opcoes.length) return sozinho();

  // Vence SEMPRE o maior destaque. Ja houve aqui uma preferencia por
  // "miniatura de tamanho confortavel" que dava trade ruim: em janela muito
  // baixa com 4 miniaturas ela escolhia a faixa embaixo, encolhia o destaque
  // em 2,2x e ainda deixava 2000px de largura vazios — tudo para a miniatura
  // sair com 96px em vez de 84px. Camera e tela sao o conteudo; miniatura e
  // referencia.
  const escolhida = [...opcoes].sort((a, b) => b.principal.largura - a.principal.largura)[0];
  const { principal, orientacao } = escolhida;

  // --- Fase 2: ocupa o que o destaque deixou livre ---------------------------
  let mini;
  if (orientacao === 'direita') {
    // Largura que sobrou ao lado do destaque, e o que ainda cabe empilhado.
    const sobra = largura - principal.largura - GAP;
    const cabeEmpilhado = ((altura - GAP * (n - 1)) * PROPORCAO) / n;
    mini = Math.min(sobra, cabeEmpilhado, MINI_MAX, principal.largura * MINI_FRACAO_DO_DESTAQUE);
  } else {
    // Altura que sobrou abaixo do destaque -> largura equivalente em 16:9.
    const sobraAltura = altura - principal.altura - GAP;
    const cabeLadoALado = (largura - GAP * (n - 1)) / n;
    mini = Math.min(sobraAltura * PROPORCAO, cabeLadoALado, MINI_MAX,
      principal.largura * MINI_FRACAO_DO_DESTAQUE);
  }

  // A fase 2 nunca pode ENCOLHER o que a fase 1 ja garantia que cabia.
  mini = Math.max(mini, escolhida.mini);
  if (!(mini > 0)) return sozinho();

  return {
    orientacao,
    principal,
    miniatura: { largura: mini, altura: mini / PROPORCAO },
  };
}
